import React from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Stack, Chip, Typography, Tooltip } from '@mui/material'
import CircleIcon from '@mui/icons-material/Circle'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { availableToColor, availableToBgColor } from '../../helpers'

interface Camera {
  mac: string
  label?: string
  is_reachable?: boolean
  connected?: boolean
}

interface ConnectedCamerasListProps {
  cameras: Camera[] | null | undefined
}

const ConnectedCamerasList: React.FC<ConnectedCamerasListProps> = ({ cameras }) => {
  const { t } = useTranslation()
  
  if (!cameras || cameras.length === 0) {
    return null
  }

  return (
    <Box sx={{ mt: 3 }}>
      <Stack direction="row" alignItems="center" gap={0.5} sx={{ mb: 2 }}>
        <Typography variant="h1Card">
          {t('supervision.cctv.connected_cameras')}
        </Typography>
        <Tooltip title={t('supervision.cctv.camera_connection_info')} arrow>
          <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary'}} />
        </Tooltip>
      </Stack>
      
      <Box sx={{ 
        border: '1px solid #EFEFEF',
        borderRadius: '24px',
        overflow: 'hidden'
      }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ 
            p: 1.5,
            backgroundColor: '#F9FAFB',
            borderBottom: '1px solid #EFEFEF'
          }}
        >
          <Typography fontSize={11} fontWeight={400} color="text.secondary">
            {t('supervision.cctv.camera_label')}{' '}
            <Box component="span" sx={{ fontSize: 9, color: '#9CA3AF' }}>
              (MAC)
            </Box>
          </Typography>
          <Stack direction="row" alignItems="center" gap={0.5}>
            <Typography fontSize={11} fontWeight={400} color="text.secondary">
              {t('supervision.cctv.connection_status')}
            </Typography>
            <Tooltip title={t('supervision.cctv.connection_status_tooltip')} arrow>
              <InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.secondary'}} />
            </Tooltip>
          </Stack>
        </Stack>

        {cameras.map((camera, index) => (
          <Stack
            key={camera.mac}
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{
              p: 1.5,
              borderBottom: index < cameras.length - 1 ? '1px solid #EFEFEF' : 'none',
              '&:hover': {
                backgroundColor: '#FAFAFA'
              }
            }}
          >
            <Stack direction="row" gap={1} alignItems="center">
              <CircleIcon
                sx={{
                  fontSize: '10px',
                  color: camera.is_reachable ? availableToColor('up') : availableToColor('down')
                }}
              />
              <Stack direction="row" gap={0.5} alignItems="baseline">
                <Typography fontSize={12} fontWeight={500}>
                  {camera.label || camera.mac}
                </Typography>
                {camera.label && (
                  <Typography fontSize={10} fontWeight={400} color="#9CA3AF">
                    ({camera.mac})
                  </Typography>
                )}
              </Stack>
            </Stack>
            
            <Chip
              label={camera.connected ? t('supervision.cctv.connected') : t('supervision.cctv.disconnected')}
              size="small"
              sx={{
                height: 24,
                fontSize: 10,
                fontWeight: 500,
                backgroundColor: camera.connected ? availableToBgColor('up') : availableToBgColor('down'),
                color: camera.connected ? availableToColor('up') : availableToColor('down'),
                border: 'none',
                width: 'fit-content',
                '& .MuiChip-label': {
                  padding: '0 8px'
                }
              }}
            />
          </Stack>
        ))}
      </Box>
    </Box>
  )
}

export default ConnectedCamerasList